import ApiClient from '../core/ApiClient';
import {
    getProductDurationDays,
    getProductDurationUnit,
} from './productDuration.js';

function normalizeTranslated(value = {}) {
    return {
        ...value,
        title: value?.title ?? '',
        description: value?.description ?? ''
    };
}

function isProductEnabled(product) {
    const enabled = product?.enabled;
    return enabled !== false && enabled !== 0 && enabled !== '0';
}

function normalizeProduct(product, category) {
    const categoryMeta = {
        id: category.id,
        value: category.value,
        path: category.path,
        thumbnail: category.thumbnail,
        translated: normalizeTranslated(category.translated),
        description: category.value ?? category.description ?? ''
    };

    /**
     * Compute canonical days from (duration, duration_type) once at
     * normalization time so downstream consumers (sort, icon picker, label
     * formatters) all see the same value without re-parsing.
     */
    const durationCount = Number(product?.duration ?? 0);
    const durationType = getProductDurationUnit(product);
    const durationDays = getProductDurationDays(product);

    return {
        ...product,
        price: Number(product?.price ?? 0),
        duration: Number.isFinite(durationCount) ? durationCount : 0,
        duration_type: durationType,
        duration_days: durationDays,
        enabled: isProductEnabled(product),
        translated: {
            ...(product?.translated || {}),
            title: product?.translated?.title ?? product?.title ?? product?.name ?? '',
            description: product?.translated?.description ?? product?.description ?? ''
        },
        name: product?.name ?? product?.title ?? '',
        category: {
            ...categoryMeta,
            ...(product?.category && typeof product.category === 'object' ? product.category : {})
        }
    };
}

function sortProductsByDuration(products) {
    return [...products].sort((a, b) => {
        /** Sort by canonical days so 7 < 30 < 180 < 365 across mixed units. */
        const durationDiff = getProductDurationDays(a) - getProductDurationDays(b);
        if (durationDiff !== 0) return durationDiff;
        return Number(a?.id || 0) - Number(b?.id || 0);
    });
}

export function normalizeProductCategoriesResponse(payload) {
    const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
            ? payload
            : [];

    return rows
        .filter((category) => category && typeof category === 'object')
        .map((category, index) => {
            const products = Array.isArray(category.products)
                ? category.products
                    .filter((product) => product && typeof product === 'object' && isProductEnabled(product))
                    .map((product) => normalizeProduct(product, category))
                : [];

            return {
                ...category,
                index: Number(category.index ?? index),
                description: category.value ?? category.description ?? '',
                translated: normalizeTranslated(category.translated),
                products: sortProductsByDuration(products)
            };
        });
}

export function flattenProductsFromCategories(categories) {
    return (Array.isArray(categories) ? categories : []).flatMap((category) =>
        Array.isArray(category?.products) ? category.products : []
    );
}

export function normalizeCatalogPath(value) {
    if (value == null || value === '') return '';
    const raw = String(value).trim();
    if (!raw) return '';

    let pathname = raw;
    try {
        const base =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : 'http://localhost';
        pathname = new URL(raw, base).pathname;
    } catch {
        pathname = raw.split('?')[0].split('#')[0];
    }

    return decodeURIComponent(pathname)
        .replace(/\/+$/, '')
        .toLowerCase();
}

function productMatches(product, criteria = {}) {
    if (!product) return false;

    if (criteria.productId != null && criteria.productId !== '') {
        if (String(product.id) === String(criteria.productId)) return true;
    }

    if (criteria.pageId != null && criteria.pageId !== '') {
        if (String(product.page_id) === String(criteria.pageId)) return true;
    }

    if (criteria.productValue != null && criteria.productValue !== '') {
        if (String(product.value) === String(criteria.productValue)) return true;
    }

    const targetPath = normalizeCatalogPath(criteria.path);
    if (targetPath) {
        const productPath = normalizeCatalogPath(product.path);
        if (productPath && productPath === targetPath) return true;
    }

    return false;
}

export function resolveProductSelection(categories, criteria = {}) {
    const list = Array.isArray(categories) ? categories : [];

    for (const category of list) {
        const products = Array.isArray(category?.products) ? category.products : [];
        const product = products.find((item) => productMatches(item, criteria));
        if (product) {
            return { category, product };
        }
    }

    const targetPath = normalizeCatalogPath(criteria.path);
    if (targetPath) {
        const category = list.find((item) => normalizeCatalogPath(item?.path) === targetPath);
        if (category) {
            return {
                category,
                product: Array.isArray(category.products) ? category.products[0] : null
            };
        }
    }

    return { category: null, product: null };
}

export async function fetchProductCatalog(api = new ApiClient()) {
    const res = await api.request({ path: '/products/categories' });
    const categories = normalizeProductCategoriesResponse(res?.data);

    return {
        categories,
        products: flattenProductsFromCategories(categories)
    };
}
